import React from 'react';
import { FinishMark } from 'components/FinishTag';
import {
  FINISH_LABELS,
  FINISH_ORDER,
  FinishSource,
  finishFromRow,
  getAvailableFinishes,
  HaveFinish,
  wantAcceptedFinishes,
} from 'utils/finishes';
import './FinishSetCell.css';

/** Re-export: the lettered table-cell mark lives with the shared finish components. */
export { FinishMark };

/** The mark for a HAVE row (single finish, read finish-first with foil as the fallback): the lettered 18px mark. */
export const HaveFinishMark: React.FC<{ row: { finish?: HaveFinish | null; foil?: boolean | null } }> = ({ row }) => (
  <FinishMark finish={finishFromRow(row)} />
);

interface FinishSetCellProps {
  want: {
    acceptNonFoil?: boolean | null;
    acceptFoil?: boolean | null;
    acceptEtched?: boolean | null;
  };
  /**
   * The want's card, when the row carries one. An accepted finish the card does
   * not exist in is inert and never counted (Edge-Case Ruling: acceptEtched may be
   * true after the backfill on a card with no etched finish).
   */
  card?: FinishSource | null;
}

/**
 * The multi-value finish cell for a WANT row (Design Pass D-5, addendum 4.2/4.3):
 * one lettered mark plus "+n" in canonical order, so the same set always renders
 * identically; the full set is in the title; all three accepted renders the word
 * "Any" (never three tiles, never one), because a row of marks reads as a
 * restriction rather than the absence of one.
 */
export const FinishSetCell: React.FC<FinishSetCellProps> = ({ want, card }) => {
  const accepted = wantAcceptedFinishes(want);
  const possible = card ? getAvailableFinishes(card) : [...FINISH_ORDER];
  const counted = accepted.filter((f) => possible.includes(f));
  const shown = counted.length > 0 ? counted : accepted;
  const full = shown.map((f) => FINISH_LABELS[f]).join(', ');

  if (accepted.length === FINISH_ORDER.length) {
    return (
      <span className="finish-set-cell finish-set-cell--any" title={full} data-finishes={shown.join(',')}>
        Any
      </span>
    );
  }
  const [first, ...rest] = shown;
  return (
    <span className="finish-set-cell" title={full} data-finishes={shown.join(',')}>
      <FinishMark finish={first} />
      {rest.length > 0 && <span className="finish-set-cell__more">+{rest.length}</span>}
    </span>
  );
};

export default FinishSetCell;
